import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { axios } from 'api'
import {
  convertMdToText,
  isValidUrl,
  TIMEZONE,
  useCommonEntryContext
} from 'components_v2/common'
import { useMeta, useTitle } from 'hooks/useMetaTitle'
import useEventAddToCalendar from 'hooks/useEventAddToCalendar'
import { community_event_detail_view_event } from 'gtm/pushEvents'
import EventRegisterButton from 'components_v2/Events/EventRegisterButton'
import MoreEvents from './MoreEvents'
import Markdown from 'markdown-to-jsx'
import {
  Page,
  Hero,
  HeroBackdrop,
  HeroImage,
  Overlay,
  HeroContent,
  DateWrapper,
  Title,
  ContentWrapper,
  Card,
  CardFirst,
  CardSecond,
  EventName,
  Info,
  Progress,
  ProgressBar,
  BottomLinks,
  HRLine,
  AddToCalender,
  SubInfo,
  ButtonWrapper,
  SpotCount,
  MainContent,
  ProgressBarWrapper,
  Section,
  SectionTitle,
  Description,
  MapWrapper,
  MoreEventWrapper,
  ExtendedDateComponent,
  Location,
  MeetingLink,
  CalendarIcon,
  VenueTitle,
  VenueSubInfo,
  LocationIcon
} from './EventDetailView.style'
import CommonLink from 'components_v2/CommonLink'
import {
  formatEventLongDate,
  formatEventMediumDate,
  formatEventTimeRange,
  isSameEventDay
} from 'components_v2/Events/formatEvent'
import { EventStatusBadge } from './EventStatusBadge'

const timeIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    viewBox='0 0 18 18'
    fill='none'
  >
    <g clipPath='url(#clip0_118_8418)'>
      <path
        d='M9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0ZM9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1ZM9 8.29297L12.8535 12.1465L12.1465 12.8535L8 8.70703V3.5H9V8.29297Z'
        fill='black'
      />
    </g>
    <defs>
      <clipPath id='clip0_118_8418'>
        <rect width='18' height='18' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

const DateTimeContainer = ({ startAt, endAt, timezone }) => {
  if (!startAt) return null
  const isSameDay = isSameEventDay(startAt, endAt, timezone)
  return (
    <>
      <Info {...(!isSameDay && { style: { alignItems: 'center' } })}>
        <div>
          <CalendarIcon color='black' />
        </div>
        {isSameDay ? (
          <p>{formatEventLongDate(startAt, timezone)} </p>
        ) : (
          <ExtendedDateComponent
            startAt={startAt}
            endAt={endAt}
            timezone={timezone}
          />
        )}
      </Info>
      {isSameDay && (
        <Info>
          <div>{timeIcon}</div>
          <div>{formatEventTimeRange(startAt, endAt, timezone)}</div>
        </Info>
      )}
    </>
  )
}
const EventDetailView = () => {
  const { id } = useParams()
  const { setNotFound } = useCommonEntryContext()
  const [event, setEvent] = useState(null)
  const [upcomingEventsData, setUpcomingEventsData] = useState({})
  const [isError, setIsError] = useState(false)
  const { AddToCalendarDialog, handleAddToCalendarClick } =
    useEventAddToCalendar()

  const fetchEvent = useCallback(
    (load = false) => {
      axios
        .get(`/events/${id}?timezone=${TIMEZONE}`, { load: load === true })
        .then((res) => {
          setEvent(res?.data?.event)
          const event = res.data?.event
          if (event) {
            community_event_detail_view_event({
              id: event.id,
              title: event.title,
              locationType: event.locationType,
              isPast: new Date(event.endAt).getTime() < Date.now()
            })
          }
          setNotFound(!res?.data?.event)
        })
        .catch((err) => {
          console.error(err)
          setIsError(true)
          setNotFound(true)
          return null
        })
    },
    [id, setNotFound]
  )

  const regionName = event?.region
  const countryName = event?.country

  const fetchEventData = useCallback(() => {
    if (!regionName && !countryName) return

    const params = new URLSearchParams({
      sections: 'upcoming',
      per_page: '10',
      from: id,
      timezone: TIMEZONE,
      ...(regionName && { region: regionName }),
      ...(countryName && { country: countryName })
    })

    axios
      .get(`/events?${params.toString()}`, { load: true })
      .then((res) => {
        const upcomingData = res.data?.upcoming?.data || []
        setUpcomingEventsData(upcomingData.filter((i) => i.id !== id))
      })
      .catch((err) => {
        console.error(err)
        setIsError(true)
        setUpcomingEventsData([])
      })
  }, [id, regionName, countryName])

  useEffect(() => {
    setIsError(false)
    fetchEvent(true)
  }, [fetchEvent])

  useEffect(() => {
    fetchEventData()
  }, [fetchEventData])

  useTitle(event?.title ? `${event.title} | Community Events` : '')
  useMeta(
    event?.title || event?.description
      ? [
          {
            name: 'description',
            content:
              convertMdToText(event.description?.slice(0, 160)) ||
              `Community event details for ${event.title}.`
          }
        ]
      : []
  )

  const {
    usedSlots = 0,
    title = '',
    description = '',
    coverUrl = '',
    startAt = '',
    endAt = '',
    locationType = '',
    locationDescription = '',
    timezone = '',
    city = '',
    region = '',
    venue = '',
    country = '',
    fullAddress = '',
    meetingUrl = '',
    hosts = [],
    salesEnd = null,
    salesStart = null,
    registrationQuestions = [],
    registrationDefaults = {},
    registrationStatus = '',
    spotsRemaining = null,
    lumaUrl = ''
  } = event ?? {}

  const heroRef = useRef(null)
  const [heroLowRes, setHeroLowRes] = useState(false)

  // Luma cover images can come back smaller than the banner. Probe the natural
  // size and, when it would have to upscale to fill, render it "contained"
  // (sharp, at native size) over a blurred backdrop instead of stretching it.
  useEffect(() => {
    if (!coverUrl) {
      setHeroLowRes(false)
      return undefined
    }

    let active = true
    const probe = new window.Image()
    probe.onload = () => {
      if (!active) return
      const el = heroRef.current
      const targetWidth = el?.clientWidth || window.innerWidth
      const targetHeight = el?.clientHeight || 0
      const lowRes =
        probe.naturalWidth < targetWidth * 0.9 ||
        (targetHeight > 0 && probe.naturalHeight < targetHeight * 0.9)
      setHeroLowRes(lowRes)
    }
    probe.onerror = () => {
      if (active) setHeroLowRes(false)
    }
    probe.src = coverUrl

    return () => {
      active = false
    }
  }, [coverUrl])

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 0)
  }, [id])

  const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(
    fullAddress
  )}&output=embed`
  const isPast = new Date(endAt).getTime() < Date.now()

  const maxSlots = spotsRemaining === null ? 0 : spotsRemaining + usedSlots
  const localityLine = [city, region, country].filter(Boolean).join(', ')

  const eventCalendarData = {
    id,
    title,
    description,
    coverUrl,
    startAt,
    endAt,
    timezone,
    locationType,
    city,
    region,
    venue,
    country,
    fullAddress,
    meetingUrl,
    hosts,
    lumaUrl
  }

  const handleClick = (event) => {
    event?.preventDefault()
    event.stopPropagation()
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('source') === 'iosApp') {
      window.webkit?.messageHandlers?.tokenHandler?.postMessage(
        eventCalendarData
      )
    } else {
      handleAddToCalendarClick(eventCalendarData)
    }
  }

  if (isError || !event) {
    return null
  }

  return (
    <Page>
      <Hero ref={heroRef}>
        {heroLowRes && <HeroBackdrop imageUrl={coverUrl} />}
        {coverUrl && (
          <HeroImage src={coverUrl} alt={title} $contain={heroLowRes} />
        )}
        <Overlay />
        <HeroContent>
          <DateWrapper>{formatEventMediumDate(startAt, timezone)}</DateWrapper>
          <Title>{title}</Title>
        </HeroContent>
      </Hero>
      {AddToCalendarDialog}
      <ContentWrapper>
        <Card>
          <CardFirst>
            {!isPast && (
              <EventStatusBadge
                usedSlots={usedSlots}
                maxSlots={maxSlots}
                salesEnd={salesEnd}
                salesStart={salesStart}
                registrationStatus={registrationStatus}
              />
            )}
            <EventName>{title}</EventName>
            <DateTimeContainer
              startAt={startAt}
              endAt={endAt}
              timezone={timezone}
            />

            {venue && (
              <Info>
                <div>
                  <LocationIcon />
                </div>
                <div>
                  <div>{venue}</div>
                  {localityLine && <SubInfo>{localityLine}</SubInfo>}
                </div>
              </Info>
            )}
          </CardFirst>
          <CardSecond>
            {Number(spotsRemaining) > 0 && (
              <ProgressBarWrapper>
                <Progress>
                  <ProgressBar percentage={(usedSlots / maxSlots) * 100} />
                </Progress>
                <SpotCount>{maxSlots - usedSlots} left</SpotCount>
              </ProgressBarWrapper>
            )}
            {!isPast && (
              <ButtonWrapper>
                <EventRegisterButton
                  {...event}
                  eventId={id}
                  eventTitle={title}
                  registrationQuestions={registrationQuestions}
                  registrationDefaults={registrationDefaults}
                  registrationStatus={registrationStatus}
                  isPast={isPast}
                  showCancelButton={true}
                  onRegistrationChange={fetchEvent}
                />
              </ButtonWrapper>
            )}
            {!isPast && <HRLine />}
            <BottomLinks>
              <CommonLink to='/events'>View All Events</CommonLink>
              {!isPast && (
                <AddToCalender>
                  <a href='#/' onClick={handleClick}>
                    Add to Calendar
                  </a>
                  <div>
                    <CalendarIcon color='black' />
                  </div>
                </AddToCalender>
              )}
            </BottomLinks>
          </CardSecond>
        </Card>
        <MainContent>
          {description && (
            <Section>
              <SectionTitle>ABOUT THIS EVENT</SectionTitle>
              <Description className='markdown'>
                <Markdown
                  options={{
                    overrides: {
                      a: {
                        props: {
                          target: '_blank',
                          rel: 'noopener noreferrer'
                        }
                      }
                    }
                  }}
                >
                  {description}
                </Markdown>
              </Description>
            </Section>
          )}
          {venue && (
            <Section>
              <SectionTitle>VENUE</SectionTitle>
              <VenueTitle>{venue}</VenueTitle>
              {localityLine && <VenueSubInfo>{localityLine}</VenueSubInfo>}
              {locationDescription && (
                <Description>{locationDescription}</Description>
              )}
            </Section>
          )}
          {locationType === 'offline' ? (
            <Section
              style={{
                border: '0px',
                marginBottom: '24px',
                paddingBottom: '0px'
              }}
            >
              <SectionTitle>Location</SectionTitle>
              <MapWrapper>
                <iframe
                  title='Google Map'
                  src={mapUrl}
                  loading='lazy'
                  allowFullScreen
                  referrerPolicy='no-referrer-when-downgrade'
                />
              </MapWrapper>
              <Location>{fullAddress}</Location>
            </Section>
          ) : (
            <Section style={{ border: '0px' }}>
              <SectionTitle>This is an online event</SectionTitle>
              {meetingUrl && (
                <MeetingLink>
                  Link :{' '}
                  {isValidUrl(meetingUrl) ? (
                    <a target='_blank' href={meetingUrl} rel='noreferrer'>
                      {meetingUrl}
                    </a>
                  ) : (
                    meetingUrl
                  )}
                </MeetingLink>
              )}
            </Section>
          )}
        </MainContent>
      </ContentWrapper>

      {upcomingEventsData?.length > 0 && (
        <MoreEventWrapper>
          <Section
            style={{
              borderTop: '1px solid #A3A3A3',
              borderBottom: '0px',
              paddingTop: '24px'
            }}
          >
            <SectionTitle style={{ fontWeight: 600, fontSize: '20px' }}>
              More Events
            </SectionTitle>
            <MoreEvents events={upcomingEventsData} />
          </Section>
        </MoreEventWrapper>
      )}
    </Page>
  )
}

export default EventDetailView
